import React, { useState, useRef, useEffect, useCallback } from 'react';
import MediumButton from '../ui/MediumButton';
import ToggleButton from '../ui/ToggleButton';
import LyricsSelectionModal from '../modals/LyricsSelectionModal';
import PromptInput from './PromptInput';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import {
  WandIcon,
  BookmarkIcon,
  SuccessIcon,
  CloseIcon,
  EditUndoIcon,
  ClearIcon,
  BookmarkOutlineIcon,
  SearchIcon,
  LyricsIcon,
  ExpandContentIcon,
  CollapseContentIcon,
  ChevronDownIcon
} from '../../utils/icons';

import {
  SmallSpinner
} from '../../utils/loaders'

// --- Child Components ---

const SaveLyricsModal = ({ onSave, onClose }) => {
    const [title, setTitle] = useState('');
    
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="w-[366px] flex flex-col rounded-3xl overflow-hidden" style={{ backgroundColor: colors.bg.secondary }}>
                <div className="flex justify-between items-center p-6">
                    <div className="w-10 h-10 opacity-0"></div>
                    <h2 className="text-lg font-semibold text-[#faf7f5]">Save Lyrics</h2>
                    <button onClick={onClose} className="flex justify-center items-center w-10 h-10 rounded-full bg-[#363030] hover:bg-opacity-80 transition-colors">
                        <CloseIcon />
                    </button>
                </div>
                <div className="flex flex-col gap-4 px-6">
                    <p className="text-xs text-center text-[#bab4b1]">Give your lyrics a title so it’s easy to remember</p>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Lyrics title"
                        className="h-14 bg-transparent text-center text-sm font-medium text-[#faf7f5] placeholder:text-[#726e6c] px-4 focus:outline-none border border-white/15 rounded-xl"
                    />
                </div>
                <div className="p-6">
                    <button onClick={() => onSave(title)} className="w-full h-[56px] flex justify-center items-center rounded-full bg-[#faf7f5] text-[#0e0808] text-lg font-semibold hover:bg-opacity-90 transition-colors">
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const SavedLyricsModal = ({ savedLyrics, onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredHistory = savedLyrics.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.lyrics.toLowerCase().includes(searchTerm.toLowerCase())
    ).reverse();

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="w-[450px] flex flex-col rounded-3xl overflow-hidden" style={{ backgroundColor: colors.bg.secondary }}>
                 <div className="flex justify-between items-center p-6">
                    <div className="w-10 h-10 opacity-0"></div>
                    <h2 className="text-lg font-semibold text-[#faf7f5]">Saved Lyrics</h2>
                    <button onClick={onClose} className="flex justify-center items-center w-10 h-10 rounded-full bg-[#363030] hover:bg-opacity-80 transition-colors">
                        <CloseIcon />
                    </button>
                </div>
                <div className="px-6 pb-6 flex flex-col gap-4">
                    <div className="flex-grow h-10 relative rounded-full border border-white/15 overflow-hidden flex items-center px-4 gap-2">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search"
                            className="w-full bg-transparent text-base font-medium text-[#faf7f5] placeholder:text-[#726e6c] focus:outline-none"
                        />
                    </div>
                    <div className="max-h-[400px] overflow-y-auto flex flex-col gap-2">
                        {filteredHistory.map((item, index) => (
                             <div key={index} onClick={() => onSelect(item.lyrics)} className="p-4 rounded-lg bg-[#363030] cursor-pointer hover:bg-opacity-80 flex items-center gap-4">
                                <LyricsIcon/>
                                <div>
                                    <p className="text-base font-medium text-[#faf7f5] truncate">{item.title}</p>
                                    <p className="text-xs text-[#bab4b1] mt-1">{new Date(item.timestamp).toLocaleDateString()}</p>
                                </div>
                             </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};



const LyricsGenerator = ({ onGenerate, lyrics, onLyricsChange, onUndo, onClear, onStar, isStarred, setIsStarred, isInstrumental, onToggleInstrumental, isAuto, onToggleAuto, onRecentsClick, hasSavedLyrics, isGenerating, onInlineEnhance, isFromPromptGeneration = false, isExpanded, onToggleExpand, isCollapsed, onToggleCollapse, isIntroMode = false, introLength = 3 }) => {
  const [isEnhanceMode, setIsEnhanceMode] = useState(false);
  const [selection, setSelection] = useState(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isShimmering, setIsShimmering] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [componentHeight, setComponentHeight] = useState(null);
  const textareaRef = useRef(null);
  const componentRef = useRef(null);
  

  const handleGenerate = () => {
      if (prompt) {
          onGenerate(prompt, lyrics, selection);
          setPrompt('');
          setIsEnhanceMode(false);
          setHasSelection(false);
          setSelection(null);
      }
  };

  const handleTextSelect = () => {
    const textarea = textareaRef.current;
    if(textarea) {
        const { selectionStart, selectionEnd, value } = textarea;
        const start = Math.min(selectionStart, selectionEnd);
        const end = Math.max(selectionStart, selectionEnd);
        if (start !== end) {
            const selectedText = value.substring(start, end);
            setSelection({ start, end, text: selectedText });
            setHasSelection(true);
        } else {
            setSelection(null);
            setHasSelection(false);
        }
    }
  };

  const renderLyricsWithHighlight = () => {
    if (!lyrics || !selection || !isEnhanceMode) {
      return lyrics;
    }

    const { start, end } = selection;
    const before = lyrics.substring(0, start);
    const selected = lyrics.substring(start, end);
    const after = lyrics.substring(end);

    return (
      <>
        {before}
        <span 
          className={`bg-[#FD429C] text-[#FAF7F5] px-1 ${isShimmering ? 'shimmer' : ''}`}
          style={{
            fontSize: 'inherit',
            lineHeight: 'inherit',
            backgroundColor: '#FD429C',
            padding: isExpanded ? '0px 0px' : '0 0px',
            fontWeight: isExpanded ? 300 : 400
          }}
        >
          {selected}
        </span>
        {after}
      </>
    );
  };
  
  const handleTextChange = (e) => {
    onLyricsChange?.(e.target.value);
    setIsStarred(false);
    setHasSelection(false);
    setSelection(null);
  }
  
  const placeholderText = hasSelection 
    ? "Enhance selection (e.g., make it happier)"
    : "Enhance lyrics (e.g. make it sound happier)";
    
  const showTextArea = !isEnhanceMode || !!lyrics;
  
  // Calculate dynamic height based on lyrics content
  const calculateHeight = () => {
    if (isInstrumental) return 165;
    if (isEnhanceMode && !lyrics) return 150;
    
    const baseHeight = 200;
    const maxHeight = 350;
    const lineHeight = 24; // Approximate line height
    const minLinesForGrowth = 3; // Start growing after 3 lines
    
    if (!lyrics) return baseHeight;
    
    // Calculate both explicit line breaks and text wrapping
    const explicitLines = lyrics.split('\n').length;
    
    // Estimate wrapped lines based on character count
    // Assuming approximately 50-60 characters per line (varies by font size)
    const avgCharsPerLine = isExpanded ? 35 : 50; // Expanded mode has larger font
    const totalLines = lyrics.split('\n').reduce((total, line) => {
      const wrappedLines = Math.max(1, Math.ceil(line.length / avgCharsPerLine));
      return total + wrappedLines;
    }, 0);
    
    // Use the larger of explicit lines or total estimated lines
    const effectiveLines = Math.max(explicitLines, totalLines);
    
    if (effectiveLines <= minLinesForGrowth) return baseHeight;
    
    const additionalLines = effectiveLines - minLinesForGrowth;
    const calculatedHeight = baseHeight + (additionalLines * lineHeight);
    
    return Math.min(calculatedHeight, maxHeight);
  };
  
  const dynamicHeight = calculateHeight();
  const [bounceAnimation, setBounceAnimation] = useState(false);
  
  const analyzeTextWithGemini = async (text) => {
    const apiPrompt = `Analyze the following text and determine if it's a prompt/description for generating lyrics OR if it's actual song lyrics.

Text: "${text}"

Return only "PROMPT" if it's a request/description for generating lyrics (like "write a song about love", "happy pop song", "breakup ballad", etc.)
Return only "LYRICS" if it contains actual song lyrics (with verses, choruses, or lyrical content)

Response:`;

    const payload = {
      contents: [{ role: "user", parts: [{ text: apiPrompt }] }],
      generationConfig: {
        responseMimeType: "text/plain"
      }
    };

    const apiKey = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU";
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      
      if (result.candidates && result.candidates[0].content.parts.length > 0) {
        const analysis = result.candidates[0].content.parts[0].text.trim().toUpperCase();
        return analysis.includes("PROMPT") ? "PROMPT" : "LYRICS";
      }
    } catch (error) {
      console.error("Error analyzing text:", error);
    }
    
    // Default to lyrics if analysis fails
    return "LYRICS";
  };

  const handleWandClick = async () => {
    if (!lyrics && !isEnhanceMode) {
      setBounceAnimation(true);
      setTimeout(() => setBounceAnimation(false), 300);
      onGenerate("Generate original song lyrics", "", null, true);
    } else if (lyrics && !isEnhanceMode) {
      // Analyze the text to determine if it's a prompt or lyrics
      const textType = await analyzeTextWithGemini(lyrics);
      
      if (textType === "PROMPT") {
        // If it's a prompt, generate lyrics directly
        onGenerate(lyrics, "", null, true);
      } else {
        // If it's lyrics, show enhance mode
        setIsEnhanceMode(true);
      }
    } else {
      setIsEnhanceMode(!isEnhanceMode);
    }
  };

  // Drag functionality
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const startY = e.clientY;
    const startHeight = componentRef.current ? componentRef.current.offsetHeight : dynamicHeight;

    const handleDragMove = (moveEvent) => {
      moveEvent.preventDefault();
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY));
      setComponentHeight(newHeight);
      
      // Immediately update the component style for smoother dragging
      if (componentRef.current) {
        componentRef.current.style.height = `${newHeight}px`;
      }
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      
      // Re-enable transitions after dragging
      if (componentRef.current) {
        componentRef.current.style.transition = 'all 200ms ease-in-out';
      }
    };

    // Disable transitions during dragging for smoother experience
    if (componentRef.current) {
      componentRef.current.style.transition = 'none';
    }

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [dynamicHeight]);

  // Touch drag functionality
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const startY = e.touches[0].clientY;
    const startHeight = componentRef.current ? componentRef.current.offsetHeight : dynamicHeight;

    const handleTouchMove = (moveEvent) => {
      moveEvent.preventDefault();
      const deltaY = moveEvent.touches[0].clientY - startY;
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY));
      setComponentHeight(newHeight);
      
      // Immediately update the component style for smoother dragging
      if (componentRef.current) {
        componentRef.current.style.height = `${newHeight}px`;
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      
      // Re-enable transitions after dragging
      if (componentRef.current) {
        componentRef.current.style.transition = 'all 200ms ease-in-out';
      }
    };

    // Disable transitions during dragging for smoother experience
    if (componentRef.current) {
      componentRef.current.style.transition = 'none';
    }

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }, [dynamicHeight]);

  const finalHeight = componentHeight !== null ? componentHeight : dynamicHeight;

  return (
    <div 
      ref={componentRef}
      className={`relative flex flex-col justify-between items-start w-full text-[#FAF7F5] font-sans overflow-visible transition-all duration-200 ease-in-out ${
        isExpanded ? 'rounded-none lyrics-expand' : 'rounded-2xl'
      }`}
      style={{
        height: isExpanded ? '100%' : isCollapsed ? '72px' : `${finalHeight}px`,
        transitionTimingFunction: isEnhanceMode && !lyrics ? 'cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        transformOrigin: 'center center',
        backgroundColor: isExpanded ? colors.bg.secondary : colors.bg.fog.thin
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      
      <div className="self-stretch flex-grow flex flex-col min-h-0">
        <div 
          className={`flex justify-between items-center self-stretch px-4 min-h-[72px] ${isCollapsed ? 'cursor-pointer' : ''}`}
          onClick={isCollapsed ? onToggleCollapse : undefined}
        >
          <div className={`flex items-center gap-2 ${!isCollapsed ? 'cursor-pointer' : ''}`} onClick={!isCollapsed ? onToggleCollapse : undefined}>
            {!isExpanded && (
              <div 
                className="flex items-center justify-center w-4 h-4 transition-transform duration-200"
                style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
              >
                <ChevronDownIcon />
              </div>
            )}
            <p style={typography.small.title}>Lyrics</p>
          </div>
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                  {!isEnhanceMode && lyrics && (
                      <>
                          <MediumButton variant="secondary" onClick={onUndo} tooltip="Undo" leftIcon={<EditUndoIcon />}></MediumButton>
                          <MediumButton variant="secondary" onClick={onClear} tooltip="Clear" leftIcon={<ClearIcon />}></MediumButton>
                          <MediumButton 
                            variant="secondary"
                            onClick={isStarred ? undefined : onStar}
                            className={isStarred ? "opacity-30 cursor-not-allowed" : ""}
                            tooltip="Save lyrics"
                            leftIcon={isStarred ? <BookmarkIcon /> : <BookmarkOutlineIcon />}
                          />
                      </>
                  )}
                   {lyrics && (
                     <MediumButton 
                       onClick={handleWandClick} 
                       variant="aura"
                       isActive={isEnhanceMode || hasSelection}
                       className="z-20"
                       tooltip="Enhance"
                       leftIcon={isGenerating ? <SmallSpinner /> : <WandIcon />}
                     />
                   )}
              </div>
            )}
        </div>
        
        {!isCollapsed && showTextArea && (
             <div className="px-4 flex-grow flex relative">
                {isEnhanceMode ? (
                  <div 
                    className="lyrics-textarea relative w-full h-full bg-transparent border-none text-[#FAF7F5] focus:outline-none resize-none whitespace-pre-wrap overflow-y-auto text-left"
                    style={{
                      fontSize: isExpanded ? '18px' : '16px',
                      lineHeight: isExpanded ? '28px' : '24px'
                    }}
                  >
                    {renderLyricsWithHighlight()}
                  </div>
                ) : (
                  <textarea 
                      ref={textareaRef} 
                      value={lyrics} 
                      onChange={handleTextChange}
                      onSelect={handleTextSelect}
                      placeholder={isInstrumental ? "Instrumental track - Lyrics disabled" : isIntroMode ? `Write some lyrics for your ${introLength} bar intro` : "Write some lyrics or a prompt"} 
                      className={`lyrics-textarea relative w-full h-full bg-transparent border-none focus:outline-none resize-none ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{
                        ...typography.medium.regular,
                        color: colors.text.primary || '#FAF7F5',
                        caretColor: colors.accent || '#FD429C',
                        fontSize: isExpanded ? '18px' : typography.medium.regular.fontSize,
                        lineHeight: isExpanded ? '28px' : typography.medium.regular.lineHeight
                      }}
                      disabled={isInstrumental || isGenerating}
                  />
                )}
             </div>
        )}
      </div>
      
      {!isCollapsed && isEnhanceMode ? (
        <div 
          className="absolute inset-0 z-10"
          onClick={(e) => {
            // Only exit if clicking outside the prompt input
            if (!e.target.closest('.prompt-input-container')) {
              setIsEnhanceMode(false);
              setHasSelection(false);
              setSelection(null);
            }
          }}
        >
          <div className="absolute bottom-0 left-0 right-0">
            <PromptInput
              onSubmit={(value) => {
                // If we have a selection, do inline enhancement
                if (hasSelection && selection) {
                  const prompt = value || "Enhance and improve this text";
                  setIsShimmering(true);
                  onInlineEnhance(prompt, selection).then(() => {
                    setIsShimmering(false);
                    setIsEnhanceMode(false);
                    setHasSelection(false);
                    setSelection(null);
                  });
                } else {
                  // Use default prompt if empty and no selection
                  const prompt = value || "Enhance and improve these lyrics";
                  onGenerate(prompt, lyrics, selection);
                  setIsEnhanceMode(false);
                  setHasSelection(false);
                  setSelection(null);
                }
              }}
              placeholder={placeholderText}
              autoFocus={true}
              requireInput={hasSelection}
              showTimer={false}
              timerDuration={3}
              fontSize={isExpanded ? '16px' : undefined}
            />
          </div>
        </div>
      ) : (
        !isCollapsed && (!lyrics || isInstrumental) && !isExpanded && (
          <div className="flex items-center self-stretch gap-2 p-4">
            {!isInstrumental && hasSavedLyrics && <MediumButton onClick={onRecentsClick} leftIcon={<BookmarkIcon />}></MediumButton>}
            <div className="flex items-center h-10 gap-2">
                <ToggleButton 
                  isActive={isInstrumental} 
                  onToggle={onToggleInstrumental}
                  text="Instrumental"
                  leftIcon={<SuccessIcon isActive={isInstrumental} />}
                >
                </ToggleButton>
            </div>
          </div>
        )
      )}
      
      {/* Absolute positioned Expand/Minimize button - Hidden when instrumental is active or collapsed */}
      {!isInstrumental && !isCollapsed && (
        <div className="absolute bottom-4 right-4">
          <MediumButton 
            onClick={onToggleExpand}
            variant={isExpanded ? "primary" : "secondary"}
            leftIcon={isExpanded ? <CollapseContentIcon /> : <ExpandContentIcon />}
          />
        </div>
      )}

      {/* Drag Handle - Hidden when expanded, instrumental, or collapsed */}
      {!isExpanded && !isInstrumental && !isCollapsed && (isHovered || isDragging) && (
        <div
          className="absolute left-1/2 transform -translate-x-1/2 cursor-ns-resize"
          style={{
            bottom: '-0px',
            width: '48px',
            height: '16px',
            paddingTop: '8px'
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleTouchStart}
        >
          <div
            className="w-12 h-1 rounded-full mx-auto"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0px 4px 12px 0 rgba(0,0,0,0.15)'
            }}
          />
        </div>
      )}
    </div>
  );
};

// --- App Component (Top Level) ---
export default function LyricsComponent({
  lyrics: externalLyrics = '',
  onLyricsChange,
  isIntroMode = false,
  introLength = 3,
  isCollapsed: defaultCollapsed = false,
  collapsed,
  onCollapsedChange
}) {
  const [lyrics, setLyrics] = useState(externalLyrics);
  const [isFromPromptGeneration, setIsFromPromptGeneration] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  // Use controlled state if provided, otherwise use internal state
  const isCollapsed = collapsed !== undefined ? collapsed : internalCollapsed;
  const handleToggleCollapse = onCollapsedChange || (() => setInternalCollapsed(!internalCollapsed));

  // Sync external lyrics changes
  useEffect(() => {
    setLyrics(externalLyrics);
  }, [externalLyrics]);

  // Sync collapsed state with prop changes
  useEffect(() => {
    if (collapsed === undefined) {
      setInternalCollapsed(defaultCollapsed);
    }
  }, [defaultCollapsed, collapsed]);

  // Handle ESC key to exit expanded mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);
  const [lyricsHistory, setLyricsHistory] = useState([]);
  const [savedLyrics, setSavedLyrics] = useState([]);
  const [isStarred, setIsStarred] = useState(false);
  const [isInstrumental, setIsInstrumental] = useState(false);
  const [isAuto, setIsAuto] = useState(false);
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const [showRecentsModal, setShowRecentsModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLyrics, setGeneratedLyrics] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [currentSelection, setCurrentSelection] = useState(null);
  const [originalTextForDiff, setOriginalTextForDiff] = useState(null);

  const handleInlineEnhance = async (prompt, selection) => {
    setLyricsHistory(prev => [...prev, { lyrics, timestamp: Date.now() }]);
    
    const apiPrompt = `I have the following lyrics:\n\n"${lyrics}"\n\nThe user has selected this portion: "${selection.text}".\n\nPlease rewrite ONLY the selected portion based on this instruction: "${prompt}". Return only the rewritten text, nothing else.`;
    
    let chatHistory = [{ role: "user", parts: [{ text: apiPrompt }] }];
    const payload = {
      contents: chatHistory,
      generationConfig: {
        responseMimeType: "text/plain"
      }
    };
    
    const apiKey = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU"; 
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      
      if (result.candidates && result.candidates[0].content.parts.length > 0) {
        const enhancedText = result.candidates[0].content.parts[0].text.trim();
        const { start, end } = selection;
        const newLyrics = lyrics.substring(0, start) + enhancedText + lyrics.substring(end);
        setLyrics(newLyrics);
        onLyricsChange?.(newLyrics);
        setIsStarred(false);
      }
    } catch (error) {
      console.error("Error enhancing text:", error);
    }
  };

  const updateLyrics = (newLyrics) => {
    setLyricsHistory(prev => [...prev, { lyrics, timestamp: Date.now() }]);
    setLyrics(newLyrics);
    onLyricsChange?.(newLyrics);
    setIsStarred(false);
  };
  
  const handleUndo = () => {
      if(lyricsHistory.length > 0) {
          const previousState = lyricsHistory[lyricsHistory.length - 1];
          setLyrics(previousState.lyrics);
          onLyricsChange?.(previousState.lyrics);
          setLyricsHistory(prev => prev.slice(0, -1));
          const isNowStarred = savedLyrics.some(saved => saved.lyrics === previousState.lyrics);
          setIsStarred(isNowStarred);
      }
  };

  const handleClear = () => {
    if(lyrics){
        setLyricsHistory(prev => [...prev, { lyrics, timestamp: Date.now() }]);
        setLyrics('');
        onLyricsChange?.('');
        setIsStarred(false);
    }
  };
  
  const handleToggleInstrumental = () => {
    setIsInstrumental(prev => {
        const isNowInstrumental = !prev;
        if (isNowInstrumental) {
            if (lyrics) {
                setLyricsHistory(h => [...h, { lyrics, timestamp: Date.now() }]);
            }
            setLyrics('');
            onLyricsChange?.('');
            setIsStarred(false);
        }
        return isNowInstrumental;
    });
  };

  const handleGenerate = async (prompt, existingLyrics = '', selection = null, isFromPrompt = false) => {
    if(existingLyrics && (!selection || !selection.text)) {
        setLyricsHistory(prev => [...prev, { lyrics: existingLyrics, timestamp: Date.now() }]);
    }
    setShowLyricsModal(true);
    setIsGenerating(true);
    setCurrentPrompt(prompt);
    setCurrentSelection(selection);
    setIsFromPromptGeneration(isFromPrompt);

    if (selection) {
      setOriginalTextForDiff(selection.text);
    } else if (existingLyrics && !isFromPrompt) {
      setOriginalTextForDiff(existingLyrics);
    } else {
      setOriginalTextForDiff(null);
    }

    let apiPrompt;
    if (selection && selection.text) {
      apiPrompt = `I have the following lyrics:\n\n"${existingLyrics}"\n\nThe user has selected this portion: "${selection.text}".\n\nPlease rewrite ONLY the selected portion based on this instruction: "${prompt}". Provide two new, distinct versions of the rewritten selection. For each version in the JSON response, you MUST provide an empty string for the "title" field and the rewritten text for the "lyrics" field.`;
    } else if (existingLyrics) {
      apiPrompt = `I have these lyrics:\n\n"${existingLyrics}"\n\nPlease generate two new, distinct versions of these lyrics based on the following instruction: "${prompt}". For each version in the JSON response, you MUST provide an empty string for the "title" field and the full modified lyrics for the "lyrics" field. Keep the structure simple with only [Verse 1], [Chorus], and [Verse 2] sections.`;
    } else {
      apiPrompt = `Generate two distinct versions of song lyrics based on the following theme: '${prompt}'. For each version in the JSON response, you MUST provide an empty string for the "title" field and the lyrics for the "lyrics" field. Keep the structure simple with only [Verse 1], [Chorus], and [Verse 2] sections.`;
    }

    let chatHistory = [{ role: "user", parts: [{ text: apiPrompt }] }];
    const payload = {
      contents: chatHistory,
      generationConfig: {
        responseMimeType: "application/json",
        responseSchema: {
          type: "ARRAY",
          items: {
            type: "OBJECT",
            properties: {
              title: { "type": "STRING" },
              lyrics: { "type": "STRING" }
            },
            required: ["title", "lyrics"]
          }
        }
      }
    };
    const apiKey = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU"; 
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (result.candidates && result.candidates[0].content.parts.length > 0) {
            const parsedLyrics = JSON.parse(result.candidates[0].content.parts[0].text);
            setGeneratedLyrics(parsedLyrics.slice(0, 2));
        } else {
           setGeneratedLyrics([{ title: 'Error', lyrics: 'Could not generate lyrics. Please try again.' }]);
        }
    } catch (error) {
        console.error("Error generating lyrics:", error);
        setGeneratedLyrics([{ title: 'Error', lyrics: 'An error occurred while generating lyrics.' }]);
    } finally {
        setIsGenerating(false);
    }
  };
  
  const handleRegenerate = () => {
      if(currentPrompt){
          handleGenerate(currentPrompt, lyrics, currentSelection);
      }
  };

  const handleSelectLyrics = (selectedText) => {
    if (currentSelection) {
        setLyricsHistory(prev => [...prev, {lyrics: lyrics, timestamp: Date.now()}]);
        const { start, end } = currentSelection;
        const newLyrics = lyrics.substring(0, start) + selectedText + lyrics.substring(end);
        setLyrics(newLyrics);
        onLyricsChange?.(newLyrics);
    } else {
        updateLyrics(selectedText);
    }
    setShowLyricsModal(false);
    setCurrentSelection(null);
    setIsStarred(false);
  };

  const handleSaveLyrics = (title) => {
      const newSavedLyric = {title, lyrics, timestamp: Date.now()};
      setSavedLyrics(prev => [...prev, newSavedLyric]);
      setIsStarred(true);
      setShowSaveModal(false);
  }

  const handleSelectSavedLyrics = (selectedLyrics) => {
    setLyricsHistory(prev => [...prev, { lyrics, timestamp: Date.now() }]);
    setLyrics(selectedLyrics);
    onLyricsChange?.(selectedLyrics);
    setIsStarred(true);
    setShowRecentsModal(false);
  };

  const handleCloseSelectionModal = () => {
    setShowLyricsModal(false);
    setOriginalTextForDiff(null);
  };


  return (
    <>
      <style>{`
        .lyrics-textarea::selection {
          background-color: ${colors.accent};
          color: ${colors.text.primary};
        }
        .lyrics-textarea::placeholder {
          color: ${colors.text.inactive};
        }
        .shimmer {
          animation: shimmer 1.5s infinite;
          background: linear-gradient(90deg, ${colors.accent} 0%, rgba(253, 66, 156, 0.6) 50%, ${colors.accent} 100%);
          background-size: 200% 100%;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes expandFromCenter {
          0% { 
            transform: scale(0.98);
          }
          100% { 
            transform: scale(1);
          }
        }
        .lyrics-expand {
          animation: expandFromCenter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
      <div className={`flex items-center justify-center w-full ${
        isExpanded ? 'absolute inset-0 z-[60]' : ''
      }`}>
        <LyricsGenerator 
            onGenerate={handleGenerate} 
            lyrics={lyrics} 
            onLyricsChange={(newLyrics) => {
              setLyrics(newLyrics);
              onLyricsChange?.(newLyrics);
            }}
            onUndo={handleUndo}
            onClear={handleClear}
            onStar={() => setShowSaveModal(true)}
            isStarred={isStarred}
            setIsStarred={setIsStarred}
            isInstrumental={isInstrumental}
            onToggleInstrumental={handleToggleInstrumental}
            isAuto={isAuto}
            onToggleAuto={setIsAuto}
            onRecentsClick={() => setShowRecentsModal(true)}
            hasSavedLyrics={savedLyrics.length > 0}
            isGenerating={isGenerating}
            onInlineEnhance={handleInlineEnhance}
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            isCollapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
            isIntroMode={isIntroMode}
            introLength={introLength}
        />
        {showLyricsModal && (
          <LyricsSelectionModal 
              lyricsOptions={generatedLyrics} 
              onSelect={handleSelectLyrics} 
              onClose={handleCloseSelectionModal}
              onRegenerate={handleRegenerate}
              isGenerating={isGenerating}
              originalSelection={originalTextForDiff}
              wasFromEmptyLyrics={!lyrics && !currentSelection}
              originalLyrics={lyrics}
              currentSelection={currentSelection}
              hideBeforePanel={isFromPromptGeneration}
          />
        )}
        {showRecentsModal && (
            <SavedLyricsModal
                savedLyrics={savedLyrics}
                onSelect={handleSelectSavedLyrics}
                onClose={() => setShowRecentsModal(false)}
            />
        )}
        {showSaveModal && (
            <SaveLyricsModal
                onSave={handleSaveLyrics}
                onClose={() => setShowSaveModal(false)}
            />
        )}
      </div>
    </>
  );
}
